import { CheckmarkIcon, Col, Container, Space, Typography } from '@ag1/ui';
import { SoldOutProductsBanner } from '@portal/shared/components/SoldOutProductsBanner';
import { useIsStore } from '@portal/state/hooks/useIsStore';
import {
  getOfferAvailabilityRestriction,
  getOfferSubscriptionMedia,
  getStoreCodeFromAtlasLocale,
  useLocaleForAtlas
} from '@portal/shared/utils/offer/generic';
import { clickPurchaseIntentEvent } from 'analytics/events/studio';
import clsx from 'clsx';
import { useAtomValue, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { usePageContext } from 'processes/Page/PageContext';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { HsaFsaAvailability } from 'shared/components/HSA-FSA/HsaFsaAvailability';
import { offerSectionAtom } from 'state/atoms/offerSectionAtom';
import { OFFER_MEDIA_DEVICE_TYPE, OFFER_TRANSACTION, OfferCoreProductMedia } from '@ag1/atlas';
import commonStyles from '../../styles.module.scss';
import { OfferButtonDisabledReason, useOfferHeadline } from '../common';
import styles from './OfferPrice.module.scss';
import { OfferPriceTable } from './OfferPriceTable';
import {
  ContentBlockButton,
  ContentBlockProps,
  ResponsiveContentBlockImage,
  useContentBlockOfferPriceRowsData,
  useOfferContentBlock
} from '../../shared';
import { useOfferPriceHook } from './hook';

const { MultipleOptionsOffer } = OFFER_TRANSACTION;
const { Desktop, Mobile } = OFFER_MEDIA_DEVICE_TYPE;

const COL_SPAN = { xxs: 12, m: 6 };

type Props = ContentBlockProps;

export const OfferPrice: React.FC<Props> = ({ slug, customContent, visibleOnPageLoad }) => {
  const router = useRouter();
  const atlasLocale = useLocaleForAtlas();
  const store = getStoreCodeFromAtlasLocale(atlasLocale);

  const setOfferSectionAtom = useSetAtom(offerSectionAtom);
  const offerSectionAtomValue = useAtomValue(offerSectionAtom);

  const { t } = useTranslation('offer');
  const { isUsStore } = useIsStore();

  const sectionRef = useRef<HTMLDivElement>(null);

  const {
    offer,
    coreProducts,
    addonProducts,
    singleOptionProducts,
    offerCTALink,
    shopifyVariantsByStoreCode,
    disablePrimaryCTAButtons
  } = usePageContext();

  const { getMultipleOptionsOfferPricingConfig, getSingleOptionOfferPricingConfig } =
    useOfferPriceHook();

  const {
    headline,
    backgroundColor,
    imagePosition,
    buttonLabel,
    buttonIcon,
    buttonIconPosition,
    showTrumedNote,
    showOldPriceFirst,
    captions
  } = useOfferContentBlock(customContent, { slug, router }, offer || null);

  const shopifyVariantsInCurrentStore = shopifyVariantsByStoreCode?.[store];
  const isMultipleOptionsOffer = offer?.transaction_type === MultipleOptionsOffer;

  const offerHeadline = useOfferHeadline(offer?.type, offer?.transaction_type, headline);

  const priceRowsData = useContentBlockOfferPriceRowsData({
    customContent,
    routerField: { slug, router },
    offer,
    shopifyVariantsInCurrentStore,
    coreProducts,
    addonProducts,
    singleOptionProducts
  });

  useEffect(() => {
    if (sectionRef.current) {
      setOfferSectionAtom(sectionRef.current);
    }
  }, [setOfferSectionAtom, sectionRef]);

  const availability = useMemo(
    () => getOfferAvailabilityRestriction(offer, atlasLocale),
    [offer, atlasLocale]
  );

  const offerImage = useMemo(() => {
    const locale = atlasLocale;

    const singleOptionHeroMedia =
      offer?.single_option_hero_media?.[locale]?.desktop?.sanity_reference_id &&
      offer?.single_option_hero_media?.[locale]?.mobile?.sanity_reference_id
        ? ([
            {
              type: Desktop,
              sanity_reference_id:
                offer.single_option_hero_media[locale].desktop!.sanity_reference_id
            },
            {
              type: Mobile,
              sanity_reference_id:
                offer.single_option_hero_media[locale].mobile!.sanity_reference_id
            }
          ] satisfies Pick<OfferCoreProductMedia, 'type' | 'sanity_reference_id'>[])
        : [];

    const multipleOptionMedia =
      offer?.multiple_options_core_product_selections_config?.[locale]?.[0]?.media ?? [];

    const multipleOptionFallbackConfig =
      offer?.multiple_options_core_product_fallback_media?.[locale]?.[0];
    const multipleOptionFallbackMedia = multipleOptionFallbackConfig
      ? [multipleOptionFallbackConfig]
      : [];

    const media = isMultipleOptionsOffer
      ? [...multipleOptionMedia, ...multipleOptionFallbackMedia]
      : [...singleOptionHeroMedia];

    return getOfferSubscriptionMedia(media, false);
  }, [offer, atlasLocale, isMultipleOptionsOffer]);

  const pricingConfig = useMemo(() => {
    return isMultipleOptionsOffer
      ? getMultipleOptionsOfferPricingConfig(priceRowsData)
      : getSingleOptionOfferPricingConfig(priceRowsData);
  }, [
    priceRowsData,
    isMultipleOptionsOffer,
    getMultipleOptionsOfferPricingConfig,
    getSingleOptionOfferPricingConfig
  ]);

  const handleButtonClick = useMemo(() => {
    if (!offerCTALink && disablePrimaryCTAButtons) {
      return undefined;
    }

    return () => {
      clickPurchaseIntentEvent({
        store,
        clickText: buttonLabel || undefined,
        location: 'OfferPrice',
        url: offerCTALink || undefined
      });

      if (offerSectionAtomValue && !offerCTALink) {
        offerSectionAtomValue.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        });
      }
    };
  }, [buttonLabel, disablePrimaryCTAButtons, offerCTALink, offerSectionAtomValue, store]);

  const imageComponent = useMemo(() => {
    const classes = clsx(
      styles.imageCont,
      imagePosition.position === 'right' ? styles.imageLeft : styles.imageRight
    );

    return (
      <ResponsiveContentBlockImage
        className={classes}
        image={offerImage}
        sizes={COL_SPAN}
        eagerLoading={visibleOnPageLoad}
      />
    );
  }, [offerImage, imagePosition.position, visibleOnPageLoad]);

  const shouldShowOriginalPrice = useMemo(() => {
    const defaultCoreProduct = pricingConfig.coreProduct || pricingConfig.coreProducts?.[0];
    if (!defaultCoreProduct) return false;

    // if originalPrice is set in shopify, only show if different from item price
    if (typeof defaultCoreProduct.rawOriginalPrice === 'number') {
      return defaultCoreProduct.rawOriginalPrice !== defaultCoreProduct.rawPrice;
    }

    // if originalPrice is not set in shopify, always show the fallback 20/49 values
    return true;
  }, [pricingConfig.coreProduct, pricingConfig.coreProducts]);

  return (
    <div
      className={clsx(
        styles.root,
        imagePosition.position === 'right' && styles.reverse,
        commonStyles[backgroundColor],
        'offer-price'
      )}>
      <div className={clsx(styles.header, styles.mobile)}>
        <Typography variant="headlineSans" size="2" color="black-new" noPadding>
          {offerHeadline}
        </Typography>
      </div>

      <Col className={clsx(styles.col, styles.colImage)} span={COL_SPAN}>
        {imageComponent}
      </Col>

      <Col
        id="offers"
        ref={sectionRef}
        className={clsx(
          styles.col,
          styles.colInfo,
          imagePosition.position === 'right' ? styles.colInfoLeft : styles.colInfoRight
        )}
        span={COL_SPAN}>
        <Container
          className={clsx(
            imagePosition.position === 'right' ? styles.clearRight : styles.clearLeft
          )}>
          <div className={styles.content}>
            <div className={clsx(styles.header, styles.desktop)}>
              <Typography variant="headlineSans" size="2" color="black-new" noPadding>
                {offerHeadline}
              </Typography>
            </div>

            {(pricingConfig.coreProduct || pricingConfig.coreProducts) && (
              <OfferPriceTable
                pricingConfig={pricingConfig}
                showOldPriceFirst={showOldPriceFirst}
              />
            )}
            {!!shopifyVariantsInCurrentStore && !!offer && (
              <SoldOutProductsBanner
                className={styles.proudctsSoldOutBanner}
                isOfferSoldOut={availability?.is_sold_out}
                variants={shopifyVariantsInCurrentStore}
              />
            )}
            {isUsStore && shouldShowOriginalPrice && (
              <Typography
                color="black-new"
                variant="portalCaption"
                size="1"
                noPadding
                className={styles.disclaimer}>
                <sup>†</sup> {t('offerPrice.valueOfOneTimePurchase')}
              </Typography>
            )}

            <OfferButtonDisabledReason
              style={{
                marginTop: `24px`,
                marginBottom: `-12px`
              }}
              offer={offer || null}
            />
            {buttonLabel && (
              <Space top={{ xxs: 16, s: 32, l: 40 }} className={styles.buttonWrapper}>
                <ContentBlockButton
                  className={styles.button}
                  button={{
                    label: buttonLabel,
                    link: offerCTALink || undefined
                  }}
                  fluid
                  customization={{
                    size: { xxs: 'medium', l: 'large' },
                    icon: buttonIcon,
                    iconPosition: buttonIconPosition,
                    onClick: handleButtonClick
                  }}
                />
              </Space>
            )}

            {showTrumedNote && <HsaFsaAvailability sourcePage="primaryLander" />}

            {captions.length > 0 && (
              <div className={styles.guaranteesWrapper}>
                {captions.map((item, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div key={index} className={styles.guarantee}>
                    <Space right={4} className={styles.guaranteeIcon}>
                      <CheckmarkIcon />
                    </Space>

                    <Typography color="black-new" variant="portalCaption" size="1" noPadding>
                      {item.text}
                    </Typography>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Container>
      </Col>
    </div>
  );
};
